import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ukWhen } from "@/lib/time";
import { toast } from "sonner";
import { Btn, CellMain, CellSub, Panel, St } from "@/components/portal/crm/kit";

/**
 * AskTheClient — a desk's question for the applicant, relayed by Connexis.
 *
 * The desk never emails the client itself: the question goes to the Connexis desk, they send it
 * under the Connexis brand with a private link, and the answer lands back on this case thread.
 * It is the same shape as a document request, for the things paperwork can't answer.
 */

const TONE = { raised: "amber", sent: "blue", answered: "green", cancelled: "red" };
const LABEL = {
  raised: "With the Connexis desk",
  sent: "With the client",
  answered: "Answered",
  cancelled: "Held back",
};

export function AskTheClient({ leadId, disabled, testPrefix = "lender-ask" }) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get(`/lender/leads/${leadId}/asks`); setItems(r.data.items || []); }
    catch { setItems([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [leadId]);

  const submit = async () => {
    if (q.trim().length < 8) { toast.error("Write the question out in full"); return; }
    setBusy(true);
    try {
      await api.post(`/lender/leads/${leadId}/ask`, { question: q.trim() });
      toast.success("With the Connexis desk — they'll put it to the client today");
      setQ(""); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't send");
    } finally { setBusy(false); }
  };

  return (
    <Panel title="Ask the client a question"
           sub="Anything paperwork can't answer. Connexis puts it to them in writing and the answer comes back here."
           testId={testPrefix}>
      {(items || []).length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 18 }}>
          {items.map((a) => (
            <div key={a.id} className="opt" style={{ display: "block" }} data-testid={`${testPrefix}-row-${a.id}`}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <CellMain style={{ fontSize: 14, lineHeight: 1.45 }}>&ldquo;{a.question}&rdquo;</CellMain>
                  <CellSub>
                    Raised {ukWhen(a.created_at, "—")}
                    {a.answered_at ? ` · answered ${ukWhen(a.answered_at, "")}` : ""}
                  </CellSub>
                </div>
                <St tone={TONE[a.status] || "slate"} testId={`${testPrefix}-state-${a.id}`}>
                  {LABEL[a.status] || a.status}
                </St>
              </div>
              {a.answer && (
                <div style={{ marginTop: 9, padding: "10px 13px", borderLeft: "3px solid var(--green)",
                  background: "rgba(13,148,136,.06)", borderRadius: 8, fontSize: 13.5, color: "var(--ink)" }}
                     data-testid={`${testPrefix}-answer-${a.id}`}>
                  {a.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {disabled ? (
        <div data-testid={`${testPrefix}-closed`}>
          <CellSub style={{ margin: 0 }}>
            You&apos;ve passed on this case, so it&apos;s no longer yours to ask about.
          </CellSub>
        </div>
      ) : (
        <>
          <textarea className="inp" value={q} onChange={(e) => setQ(e.target.value)} maxLength={600}
                    placeholder="e.g. What is the £40k director's loan on the last accounts for?"
                    data-testid={`${testPrefix}-question`} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
            marginTop: 11, flexWrap: "wrap" }}>
            <CellSub style={{ margin: 0 }}>
              Goes out from Connexis as written — the client answers on a private link.
            </CellSub>
            <Btn kind="primary" onClick={submit} disabled={busy || q.trim().length < 8} testId={`${testPrefix}-send`}>
              {busy ? "Sending…" : "Send it to the Connexis desk"}
            </Btn>
          </div>
        </>
      )}
    </Panel>
  );
}
